import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useCarrinho } from '../../context/CarrinhoContext';
import { useClienteAuth } from '../../context/ClienteAuthContext';
import { formatBRL } from '../../components/shared';
import MapaRaioEntrega from '../../components/MapaRaioEntrega';
import { geocodeEndereco, haversineKm } from '../../utils/geo';
import api from '../../services/api';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const TODAS_FORMAS = [
  { v: 'pix',              label: '⚡ PIX',            sub: 'Transferência instantânea' },
  { v: 'dinheiro',         label: '💵 Dinheiro',        sub: 'Pagamento em espécie' },
  { v: 'cartao',           label: '💳 Cartão',          sub: 'Débito e crédito' },
  { v: 'vale_refeicao',    label: '🍽 Vale Refeição',   sub: 'VR, Sodexo, Alelo...' },
  { v: 'vale_alimentacao', label: '🛒 Vale Alimentação', sub: 'VA, Ticket...' },
  { v: 'online',           label: '🌐 Link de Pag.',    sub: 'Pagamento online' },
];

const TODOS_TIPOS = [
  { v: 'retirada', label: '🏪 Retirar', sub: 'No local' },
  { v: 'entrega',  label: '🛵 Entrega', sub: 'No seu endereço' },
  { v: 'mesa',     label: '🪑 Mesa',    sub: 'Consumo no local' },
];

function formatCep(v) {
  const d = v.replace(/\D/g, '').slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

export default function Checkout() {
  const { empresa_id } = useParams();
  const navigate = useNavigate();
  const { itens, total, limpar } = useCarrinho();
  const { cliente, tokenCliente, logoutCliente, getPerfil, getEnderecos, postEndereco } = useClienteAuth();

  const [cfg, setCfg]     = useState(null);
  const [form, setForm]   = useState({
    nome_cliente: '', telefone: '', email: '', cpf_cliente: '',
    tipo: '', forma_pagamento: '',
    cep: '', endereco: '', numero: '', complemento: '',
    bairro: '', cidade: '', estado: '', ponto_referencia: '',
    troco_para: '', observacao: '',
  });
  const [saving, setSaving] = useState(false);
  const [pedido, setPedido] = useState(null);
  const [erros, setErros]   = useState({});

  const [enderecosSalvos, setEnderecosSalvos]       = useState([]);
  const [enderecoSelecionado, setEnderecoSelecionado] = useState(null);
  const [salvarEndereco, setSalvarEndereco]           = useState(false);
  const [buscandoCep, setBuscandoCep]                 = useState(false);

  const [enderecoCoords, setEnderecoCoords]           = useState(null);
  const [verificandoLocal, setVerificandoLocal]        = useState(false);

  const raioAtivo   = !!(cfg?.raio_entrega_ativo && cfg?.latitude != null && cfg?.longitude != null);
  const foraDoRaio  = raioAtivo && enderecoCoords
    ? haversineKm(cfg.latitude, cfg.longitude, enderecoCoords.lat, enderecoCoords.lng) > cfg.raio_entrega_km
    : false;
  const bloqueadoPorRaio = form.tipo === 'entrega' && raioAtivo && (!enderecoCoords || foraDoRaio);

  const verificarLocalizacao = async () => {
    const partes = [form.endereco, form.numero, form.bairro, form.cidade, form.estado].filter(Boolean);
    if (!partes.length) { toast.error('Preencha o endereço antes de verificar'); return; }
    setVerificandoLocal(true);
    try {
      const local = await geocodeEndereco(partes.join(', '));
      if (!local) { toast.error('Não conseguimos localizar esse endereço no mapa. Ajuste o marcador manualmente.'); return; }
      setEnderecoCoords(local);
    } catch {
      toast.error('Erro ao verificar localização');
    } finally {
      setVerificandoLocal(false);
    }
  };

  const buscarCep = async (valor) => {
    const cep = (valor || '').replace(/\D/g, '');
    if (cep.length !== 8) return;
    setBuscandoCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await resp.json();
      if (data.erro) { toast.error('CEP não encontrado'); return; }
      setForm(f => ({
        ...f,
        endereco: data.logradouro || f.endereco,
        bairro:   data.bairro     || f.bairro,
        cidade:   data.localidade || f.cidade,
        estado:   data.uf         || f.estado,
      }));
      setErros(p => ({ ...p, endereco: undefined, bairro: undefined, cidade: undefined }));

      if (raioAtivo) {
        const partes = [data.logradouro, form.numero, data.bairro, data.localidade, data.uf].filter(Boolean);
        if (partes.length) {
          setVerificandoLocal(true);
          try {
            const local = await geocodeEndereco(partes.join(', '));
            if (local) setEnderecoCoords(local);
          } catch { /* falha silenciosa, usuário pode verificar manualmente */ }
          finally { setVerificandoLocal(false); }
        }
      }
    } catch {
      toast.error('Não foi possível buscar o CEP');
    } finally {
      setBuscandoCep(false);
    }
  };

  // Carrega config da loja
  useEffect(() => {
    api.get(`/loja/${empresa_id}/status`).then(r => {
      const c = r.data;
      setCfg(c);
      setForm(f => ({
        ...f,
        tipo:           c.tipos_entrega?.[0]   || 'retirada',
        forma_pagamento: c.formas_pagamento?.[0] || 'pix',
      }));
    }).catch(() => {
      setCfg({ tipos_entrega: ['retirada','entrega'], formas_pagamento: ['pix','dinheiro','cartao'], aberta: true });
      setForm(f => ({ ...f, tipo: 'retirada', forma_pagamento: 'pix' }));
    });
  }, [empresa_id]);

  // Preenche dados do cliente buscando perfil completo da API
  useEffect(() => {
    if (!cliente) return;
    getPerfil()
      .then(({ data }) => setForm(f => ({
        ...f,
        nome_cliente: data.nome     || f.nome_cliente,
        telefone:     data.telefone || f.telefone,
        email:        data.email    || f.email,
        cpf_cliente:  data.cpf      || f.cpf_cliente,
      })))
      .catch(() => setForm(f => ({
        ...f,
        nome_cliente: cliente.nome  || f.nome_cliente,
        email:        cliente.email || f.email,
      })));
  }, [cliente, getPerfil]);

  // Carrega endereços e auto-seleciona padrão (ou único)
  useEffect(() => {
    if (!cliente || !tokenCliente) return;
    getEnderecos().then(({ data }) => {
      setEnderecosSalvos(data);
      if (!data.length) return;
      const auto = data.find(e => e.padrao) || (data.length === 1 ? data[0] : null);
      if (auto) {
        setEnderecoSelecionado(auto.id);
        setForm(f => ({
          ...f,
          cep:         auto.cep         || '',
          endereco:    auto.endereco    || '',
          numero:      auto.numero      || '',
          complemento: auto.complemento || '',
          bairro:      auto.bairro      || '',
          cidade:      auto.cidade      || '',
          estado:      auto.estado      || '',
        }));
      }
    }).catch(() => {});
  }, [cliente, tokenCliente, getEnderecos]);

  const selecionarEnderecoSalvo = useCallback((end) => {
    setEnderecoSelecionado(end.id);
    setEnderecoCoords(null);
    setForm(f => ({
      ...f,
      cep:         end.cep         || '',
      endereco:    end.endereco    || '',
      numero:      end.numero      || '',
      complemento: end.complemento || '',
      bairro:      end.bairro      || '',
      cidade:      end.cidade      || '',
      estado:      end.estado      || '',
    }));
  }, []);

  const CAMPOS_ENDERECO = ['cep', 'endereco', 'numero', 'bairro', 'cidade', 'estado'];

  const F = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    setErros(p => ({ ...p, [k]: undefined }));
    if (CAMPOS_ENDERECO.includes(k)) setEnderecoCoords(null);
  };

  const validar = () => {
    const e = {};
    if (!form.nome_cliente.trim()) e.nome_cliente = 'Nome obrigatório';
    if (!form.telefone.trim())     e.telefone     = 'Telefone obrigatório';
    if (form.tipo === 'entrega') {
      if (!form.endereco.trim()) e.endereco = 'Endereço obrigatório';
      if (!form.bairro.trim())   e.bairro   = 'Bairro obrigatório';
      if (!form.cidade.trim())   e.cidade   = 'Cidade obrigatória';
    }
    setErros(e);
    if (Object.keys(e).length) return false;

    if (form.tipo === 'entrega' && raioAtivo) {
      if (!enderecoCoords) { toast.error('Confirme sua localização no mapa para continuar'); return false; }
      if (foraDoRaio) { toast.error(`Seu endereço está fora da área de entrega (raio de ${cfg.raio_entrega_km} km)`); return false; }
    }

    const minimo = parseFloat(cfg?.valor_minimo_pedido || 0);
    if (minimo > 0 && total < minimo) {
      toast.error(`Valor mínimo do pedido: ${formatBRL(minimo)}`);
      return false;
    }

    return true;
  };

  const finalizar = async () => {
    if (!cliente) {
      toast.error('Faça login para finalizar o pedido');
      navigate(`/cliente/login?empresa_id=${empresa_id}&redirect=/${empresa_id}/checkout`);
      return;
    }
    if (!validar()) { toast.error('Preencha os campos obrigatórios'); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${tokenCliente}` };
      const { data } = await api.post('/pedidos/publico', {
        empresa_id, ...form,
        endereco_latitude:  form.tipo === 'entrega' ? enderecoCoords?.lat ?? null : null,
        endereco_longitude: form.tipo === 'entrega' ? enderecoCoords?.lng ?? null : null,
        itens: itens.map(i => ({
          produto_id:        i.id,
          descricao_produto: i.descricao,
          valor_unitario:    i.preco_promocional || i.preco_venda,
          quantidade:        i.quantidade,
          observacao:        i.observacao || null,
        })),
      }, { headers });

      if (form.tipo === 'entrega' && salvarEndereco && form.endereco) {
        try {
          await postEndereco({
            apelido: 'Entrega', cep: form.cep, endereco: form.endereco,
            numero: form.numero, complemento: form.complemento,
            bairro: form.bairro, cidade: form.cidade, estado: form.estado,
          });
          toast.success('Endereço salvo na sua conta!');
        } catch { /* não bloqueia */ }
      }

      limpar();
      setPedido(data);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao finalizar pedido');
    } finally { setSaving(false); }
  };

  const campo = (label, key, type = 'text', props = {}) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: '.8rem', fontWeight: 700, color: '#444' }}>
        {label}{props.required !== false ? ' *' : ''}
      </label>
      <input type={type} value={form[key]} onChange={e => F(key, e.target.value)}
        style={{
          padding: '12px 14px', borderRadius: 12, fontFamily: 'inherit',
          border: `1.5px solid ${erros[key] ? '#ef4444' : '#e8e4de'}`,
          fontSize: '16px', background: '#fff', outline: 'none', WebkitAppearance: 'none',
        }}
        onFocus={e => e.target.style.borderColor = '#FF6B35'}
        onBlur={e => e.target.style.borderColor = erros[key] ? '#ef4444' : '#e8e4de'}
        {...props}
      />
      {erros[key] && <span style={{ fontSize: '.75rem', color: '#ef4444' }}>{erros[key]}</span>}
    </div>
  );

  // ── Tela de confirmação ──────────────────────────────────────
  if (pedido) return (
    <div style={{ minHeight: '100dvh', background: '#F8F7F4', fontFamily: "'Sora', sans-serif",
      paddingBottom: 'env(safe-area-inset-bottom, 24px)' }}>
      <div style={{ maxWidth: 520, margin: '0 auto', padding: '40px 20px' }}>
        <div style={{ background: '#fff', borderRadius: 20, padding: 32,
          textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,.08)' }}>
          <div style={{ width: 72, height: 72, background: '#dcfce7', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', fontSize: '2rem' }}>✅</div>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 800, marginBottom: 8 }}>Pedido realizado!</h2>
          <p style={{ color: '#888', fontSize: '.875rem', marginBottom: 24 }}>
            Aguarde a confirmação do estabelecimento.
          </p>
          <div style={{ background: '#fafaf8', borderRadius: 14, padding: 20, textAlign: 'left', marginBottom: 20 }}>
            <div style={{ fontSize: '.75rem', color: '#aaa', marginBottom: 4 }}>Número do pedido</div>
            <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '.9rem', marginBottom: 16 }}>
              {pedido.id}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: '.85rem', color: '#666' }}>Tipo</span>
              <span style={{ fontSize: '.85rem', fontWeight: 700 }}>
                {pedido.tipo === 'entrega' ? '🛵 Entrega' : pedido.tipo === 'mesa' ? '🪑 Mesa' : '🏪 Retirada'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: '.85rem', color: '#666' }}>Pagamento</span>
              <span style={{ fontSize: '.85rem', fontWeight: 700 }}>
                {pedido.forma_pagamento?.replace(/_/g, ' ')}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              paddingTop: 12, borderTop: '1px solid #eee', marginTop: 12 }}>
              <span style={{ fontWeight: 700 }}>Total</span>
              <span style={{ fontWeight: 800, fontSize: '1.1rem', color: '#FF6B35' }}>
                {formatBRL(pedido.valor_total)}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Link to={`/acompanhar/${pedido.id}`} style={{
              background: '#1A1A2E', color: '#fff', borderRadius: 14,
              padding: '14px', fontWeight: 700, textAlign: 'center', textDecoration: 'none', fontSize: '1rem',
            }}>
              Acompanhar pedido
            </Link>
            <button onClick={() => navigate(`/${empresa_id}`)} style={{
              background: '#FF6B35', color: '#fff', border: 'none', borderRadius: 14,
              padding: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: '1rem',
            }}>
              Voltar ao cardápio
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!itens.length) { navigate(`/${empresa_id}`); return null; }

  const minimoValor = parseFloat(cfg?.valor_minimo_pedido || 0);
  const abaixoDoMinimo = minimoValor > 0 && total < minimoValor;

  return (
    <div style={{ minHeight: '100dvh', background: '#F8F7F4', fontFamily: "'Sora', sans-serif" }}>

      {/* ── Header ── */}
      <header style={{
        background: '#1A1A2E', padding: '14px 16px',
        paddingTop: 'max(14px, env(safe-area-inset-top))',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate(-1)} style={{
            background: 'rgba(255,255,255,.1)', border: 'none', color: '#fff',
            borderRadius: 10, width: 40, height: 40, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0,
          }}>←</button>
          <h1 style={{ color: '#fff', fontSize: '1rem', fontWeight: 800, margin: 0, flex: 1 }}>
            Finalizar Pedido
          </h1>
          <span style={{ color: '#FF6B35', fontWeight: 800, fontSize: '1rem', flexShrink: 0 }}>
            {formatBRL(total)}
          </span>
        </div>
      </header>

      {/* ── Conteúdo ── */}
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '16px 16px 0' }}>

        {/* Resumo */}
        <div style={{ background: '#fff', borderRadius: 16, padding: 16, marginBottom: 12, border: '1px solid #eee' }}>
          <div style={{ fontWeight: 800, marginBottom: 10, fontSize: '.9rem' }}>
            🛒 {itens.length} {itens.length === 1 ? 'item' : 'itens'}
          </div>
          {itens.map(i => (
            <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between',
              fontSize: '.85rem', color: '#666', marginBottom: 4 }}>
              <span>
                {i.passo_gramas && i.unidade_medida === 'KG'
                  ? `${i.quantidade.toFixed(3).replace('.', ',')} kg`
                  : `${i.quantidade}x`} {i.descricao}
                {i.observacao && (
                  <div style={{ fontSize: '.75rem', color: '#aaa', fontStyle: 'italic' }}>📝 {i.observacao}</div>
                )}
              </span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 700, color: '#333' }}>{formatBRL((i.preco_promocional || i.preco_venda) * i.quantidade)}</div>
                {i.preco_promocional && <div style={{ fontSize: '.72rem', color: '#bbb', textDecoration: 'line-through' }}>{formatBRL(i.preco_venda * i.quantidade)}</div>}
              </div>
            </div>
          ))}
          <div style={{ borderTop: '1px solid #eee', paddingTop: 10, marginTop: 10,
            display: 'flex', justifyContent: 'space-between', fontWeight: 800 }}>
            <span>Total</span>
            <span style={{ color: '#FF6B35', fontSize: '1.05rem' }}>{formatBRL(total)}</span>
          </div>
          {abaixoDoMinimo && (
            <div style={{ marginTop: 8, padding: '8px 12px', background: '#fff7ed',
              border: '1px solid #fed7aa', borderRadius: 8, fontSize: '.82rem', color: '#92400e' }}>
              ⚠️ Pedido mínimo: <strong>{formatBRL(minimoValor)}</strong>
              {' '}— faltam <strong>{formatBRL(minimoValor - total)}</strong>
            </div>
          )}
        </div>

        {/* Conta do cliente */}
        <div style={{
          background: '#fff', borderRadius: 16, padding: 20, marginBottom: 12,
          border: `1.5px solid ${!cliente ? '#fca5a5' : '#eee'}`,
        }}>
          {!cliente ? (
            <>
              <h3 style={{ fontWeight: 800, marginBottom: 6, fontSize: '1rem', color: '#991b1b' }}>
                👤 Login obrigatório
              </h3>
              <p style={{ fontSize: '.83rem', color: '#666', margin: '0 0 14px' }}>
                Faça login ou crie uma conta para finalizar seu pedido.
              </p>
              <Link to={`/cliente/login?empresa_id=${empresa_id}&redirect=/${empresa_id}/checkout`} style={{
                display: 'block', background: '#FF6B35', color: '#fff', borderRadius: 12,
                padding: '13px', fontWeight: 800, textAlign: 'center',
                textDecoration: 'none', fontSize: '.9rem',
              }}>
                Entrar / Criar conta
              </Link>
            </>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '.95rem' }}>Olá, {cliente.nome}! 👋</div>
                <div style={{ fontSize: '.78rem', color: '#888', marginTop: 2 }}>{cliente.email}</div>
              </div>
              <button onClick={() => { logoutCliente(); setForm(f => ({
                ...f, nome_cliente: '', telefone: '', email: '', cpf_cliente: '',
              })); }} style={{
                background: 'none', border: '1.5px solid #e8e4de', borderRadius: 10,
                padding: '8px 12px', fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: '.78rem', color: '#666',
              }}>
                Sair
              </button>
            </div>
          )}
        </div>

        {/* Restante só aparece quando logado */}
        {cliente && <>

          {/* Dados pessoais */}
          <div style={{ background: '#fff', borderRadius: 16, padding: 20, marginBottom: 12, border: '1px solid #eee' }}>
            <h3 style={{ fontWeight: 800, marginBottom: 14, fontSize: '1rem' }}>👤 Seus dados</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {campo('Nome completo', 'nome_cliente')}
              {campo('Telefone / WhatsApp', 'telefone', 'tel')}
              {campo('E-mail', 'email', 'email', { required: false })}
              {campo('CPF', 'cpf_cliente', 'text', { required: false })}
            </div>
          </div>

          {/* Aviso loja fechada */}
          {cfg && !cfg.aberta && (
            <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 14,
              padding: '14px 16px', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, color: '#991b1b', marginBottom: 4 }}>🔴 Loja fechada no momento</div>
              {cfg.mensagem && <div style={{ fontSize: '.82rem', color: '#b91c1c' }}>{cfg.mensagem}</div>}
              <div style={{ fontSize: '.82rem', color: '#b91c1c', marginTop: 4 }}>
                Você ainda pode montar seu pedido, mas ele será processado quando a loja reabrir.
              </div>
            </div>
          )}

          {/* Tipo de entrega */}
          <div style={{ background: '#fff', borderRadius: 16, padding: 20, marginBottom: 12, border: '1px solid #eee' }}>
            <h3 style={{ fontWeight: 800, marginBottom: 14, fontSize: '1rem' }}>🚚 Como quer receber?</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {TODOS_TIPOS.filter(t => (cfg?.tipos_entrega || ['retirada','entrega']).includes(t.v))
                .map(({ v, label, sub }) => (
                <button key={v} onClick={() => F('tipo', v)} style={{
                  padding: '14px 10px', borderRadius: 14, cursor: 'pointer',
                  border: `2px solid ${form.tipo === v ? '#FF6B35' : '#e8e4de'}`,
                  background: form.tipo === v ? '#fff1ec' : '#fafaf8',
                  textAlign: 'center', fontFamily: 'inherit', transition: 'all .15s',
                }}>
                  <div style={{ fontWeight: 700, fontSize: '.9rem', color: form.tipo === v ? '#FF6B35' : '#333' }}>
                    {label}
                  </div>
                  <div style={{ fontSize: '.72rem', color: '#999', marginTop: 3 }}>{sub}</div>
                </button>
              ))}
            </div>

            {form.tipo === 'entrega' && (
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {enderecosSalvos.length > 0 && (
                  <div style={{ marginBottom: 4 }}>
                    <div style={{ fontSize: '.8rem', fontWeight: 700, color: '#444', marginBottom: 8 }}>
                      Endereços salvos
                    </div>
                    {enderecosSalvos.map(e => (
                      <button key={e.id} onClick={() => selecionarEnderecoSalvo(e)} style={{
                        width: '100%', textAlign: 'left', padding: '12px 14px',
                        borderRadius: 12, marginBottom: 6, cursor: 'pointer',
                        border: `2px solid ${enderecoSelecionado === e.id ? '#FF6B35' : '#e8e4de'}`,
                        background: enderecoSelecionado === e.id ? '#fff1ec' : '#fafaf8',
                        fontFamily: 'inherit',
                      }}>
                        <div style={{ fontWeight: 700, fontSize: '.85rem',
                          color: enderecoSelecionado === e.id ? '#FF6B35' : '#333' }}>
                          {e.apelido}
                          {e.padrao && <span style={{ marginLeft: 6, fontSize: '.7rem',
                            background: '#FF6B35', color: '#fff', borderRadius: 8, padding: '1px 6px' }}>
                            Padrão
                          </span>}
                        </div>
                        <div style={{ fontSize: '.78rem', color: '#777', marginTop: 2 }}>
                          {[e.endereco, e.numero].filter(Boolean).join(', ')}
                          {e.bairro ? ` — ${e.bairro}` : ''}
                          {e.cidade ? `, ${e.cidade}` : ''}
                        </div>
                      </button>
                    ))}
                    <div style={{ fontSize: '.75rem', color: '#aaa', marginTop: 4, marginBottom: 8 }}>
                      Ou preencha manualmente abaixo:
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: '.8rem', fontWeight: 700, color: '#444' }}>CEP</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input
                        type="text"
                        value={form.cep}
                        onChange={e => F('cep', formatCep(e.target.value))}
                        onBlur={e => { e.target.style.borderColor = erros.cep ? '#ef4444' : '#e8e4de'; buscarCep(e.target.value); }}
                        onFocus={e => e.target.style.borderColor = '#FF6B35'}
                        placeholder="00000-000"
                        inputMode="numeric"
                        maxLength={9}
                        style={{
                          padding: '12px 14px', borderRadius: 12, fontFamily: 'inherit',
                          border: `1.5px solid ${erros.cep ? '#ef4444' : '#e8e4de'}`,
                          fontSize: '16px', background: '#fff', outline: 'none', WebkitAppearance: 'none',
                          width: '100%', boxSizing: 'border-box', paddingRight: 36,
                        }}
                      />
                      {buscandoCep && (
                        <span style={{
                          position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                          width: 16, height: 16, border: '2px solid #eee', borderTopColor: '#FF6B35',
                          borderRadius: '50%', animation: 'spin .7s linear infinite',
                        }} />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => buscarCep(form.cep)}
                      disabled={buscandoCep}
                      style={{
                        padding: '0 16px', borderRadius: 12, fontFamily: 'inherit', fontWeight: 700,
                        border: '1.5px solid #FF6B35', background: '#fff', color: '#FF6B35',
                        fontSize: '.85rem', cursor: buscandoCep ? 'not-allowed' : 'pointer', flexShrink: 0,
                      }}
                    >
                      Buscar
                    </button>
                  </div>
                </div>
                {campo('Endereço / Logradouro', 'endereco')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {campo('Número', 'numero')}
                  {campo('Complemento', 'complemento', 'text', { required: false })}
                </div>
                {campo('Bairro', 'bairro')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 12 }}>
                  {campo('Cidade', 'cidade')}
                  {campo('UF', 'estado', 'text', { required: false, maxLength: 2 })}
                </div>
                {campo('Ponto de referência', 'ponto_referencia', 'text', { required: false })}
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                  padding: '12px 14px', borderRadius: 12, border: '1.5px solid #e8e4de',
                  background: salvarEndereco ? '#fff1ec' : '#fafaf8' }}>
                  <input type="checkbox" checked={salvarEndereco}
                    onChange={e => setSalvarEndereco(e.target.checked)}
                    style={{ width: 18, height: 18, accentColor: '#FF6B35', cursor: 'pointer' }} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '.85rem',
                      color: salvarEndereco ? '#FF6B35' : '#333' }}>
                      Salvar este endereço na minha conta
                    </div>
                    <div style={{ fontSize: '.73rem', color: '#999' }}>Facilita seus próximos pedidos</div>
                  </div>
                </label>

                {raioAtivo && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <button
                      type="button"
                      onClick={verificarLocalizacao}
                      disabled={verificandoLocal}
                      style={{
                        padding: '12px 14px', borderRadius: 12, fontFamily: 'inherit', fontWeight: 700,
                        border: '1.5px solid #FF6B35', background: '#fff', color: '#FF6B35',
                        fontSize: '.875rem', cursor: verificandoLocal ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {verificandoLocal ? 'Verificando...' : '📍 Verificar localização no mapa'}
                    </button>

                    {enderecoCoords && (
                      <>
                        <div style={{ fontSize: '.75rem', color: '#999' }}>
                          Arraste o marcador se o ponto não estiver exato.
                        </div>
                        <MapaRaioEntrega
                          markerPos={[enderecoCoords.lat, enderecoCoords.lng]}
                          onMarkerChange={(lat, lng) => setEnderecoCoords({ lat, lng })}
                          radiusKm={cfg.raio_entrega_km}
                          height={260}
                          editable
                        />
                        {foraDoRaio ? (
                          <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 12,
                            padding: '10px 14px', fontSize: '.82rem', color: '#991b1b', fontWeight: 700 }}>
                            🚫 Fora da área de entrega (raio de {cfg.raio_entrega_km} km). Ajuste o marcador ou escolha retirada.
                          </div>
                        ) : (
                          <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 12,
                            padding: '10px 14px', fontSize: '.82rem', color: '#166534', fontWeight: 700 }}>
                            ✅ Endereço dentro da área de entrega
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {cfg?.tempo_entrega_min && form.tipo === 'entrega' && (
              <div style={{ marginTop: 12, fontSize: '.8rem', color: '#888', display: 'flex', alignItems: 'center', gap: 6 }}>
                🕐 Tempo estimado de entrega: <strong>{cfg.tempo_entrega_min} min</strong>
              </div>
            )}
          </div>

          {/* Pagamento */}
          <div style={{ background: '#fff', borderRadius: 16, padding: 20, marginBottom: 12, border: '1px solid #eee' }}>
            <h3 style={{ fontWeight: 800, marginBottom: 14, fontSize: '1rem' }}>💳 Pagamento</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {TODAS_FORMAS.filter(f => (cfg?.formas_pagamento || ['pix','dinheiro','cartao']).includes(f.v))
                .map(({ v, label, sub }) => (
                <button key={v} onClick={() => F('forma_pagamento', v)} style={{
                  padding: '12px 8px', borderRadius: 14, cursor: 'pointer',
                  border: `2px solid ${form.forma_pagamento === v ? '#FF6B35' : '#e8e4de'}`,
                  background: form.forma_pagamento === v ? '#fff1ec' : '#fafaf8',
                  textAlign: 'center', fontFamily: 'inherit', transition: 'all .15s',
                }}>
                  <div style={{ fontWeight: 700, fontSize: '.85rem',
                    color: form.forma_pagamento === v ? '#FF6B35' : '#333' }}>{label}</div>
                  <div style={{ fontSize: '.7rem', color: '#999', marginTop: 2 }}>{sub}</div>
                </button>
              ))}
            </div>
            {form.forma_pagamento === 'dinheiro' && (
              <div style={{ marginTop: 14 }}>
                {campo('Troco para quanto? (opcional)', 'troco_para', 'number', {
                  required: false, placeholder: 'Ex: 50,00', inputMode: 'decimal',
                })}
              </div>
            )}
          </div>

          {/* Observações */}
          <div style={{ background: '#fff', borderRadius: 16, padding: 20, marginBottom: 16, border: '1px solid #eee' }}>
            <h3 style={{ fontWeight: 800, marginBottom: 12, fontSize: '1rem' }}>📝 Observações</h3>
            <textarea value={form.observacao} onChange={e => F('observacao', e.target.value)}
              placeholder="Ex: sem cebola, pouco sal..."
              rows={3} style={{
                width: '100%', padding: '12px 14px', borderRadius: 12,
                border: '1.5px solid #e8e4de', fontFamily: 'inherit',
                fontSize: '16px', resize: 'none', outline: 'none',
                boxSizing: 'border-box', WebkitAppearance: 'none',
              }} />
          </div>

        </>}
      </div>

      {/* ── Botão rodapé ── */}
      <div style={{
        position: 'sticky', bottom: 0, background: '#F8F7F4',
        padding: '12px 16px', paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
        borderTop: '1px solid #eee', boxShadow: '0 -4px 20px rgba(0,0,0,.08)',
      }}>
        <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {!cliente ? (
            <Link to={`/cliente/login?empresa_id=${empresa_id}&redirect=/${empresa_id}/checkout`} style={{
              width: '100%', background: '#FF6B35', color: '#fff', borderRadius: 16,
              padding: '16px', fontWeight: 800, fontSize: '1.05rem', textAlign: 'center',
              textDecoration: 'none', display: 'block',
            }}>
              👤 Entrar para finalizar
            </Link>
          ) : (
            <button onClick={finalizar} disabled={saving || bloqueadoPorRaio || abaixoDoMinimo} style={{
              width: '100%', background: (saving || bloqueadoPorRaio || abaixoDoMinimo) ? '#ccc' : '#FF6B35', color: '#fff',
              border: 'none', borderRadius: 16, padding: '16px', fontWeight: 800,
              fontSize: '1.05rem', cursor: (saving || bloqueadoPorRaio || abaixoDoMinimo) ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 8,
              boxShadow: (saving || bloqueadoPorRaio || abaixoDoMinimo) ? 'none' : '0 4px 20px rgba(255,107,53,.35)',
            }}>
              {saving ? (
                <>
                  <span style={{
                    width: 18, height: 18, border: '2px solid rgba(255,255,255,.4)',
                    borderTopColor: '#fff', borderRadius: '50%',
                    animation: 'spin .7s linear infinite', display: 'inline-block',
                  }} />
                  Enviando...
                </>
              ) : '✅ Confirmar Pedido'}
            </button>
          )}
          <button onClick={() => navigate(-1)} style={{
            width: '100%', background: 'none', border: 'none', color: '#888',
            cursor: 'pointer', fontSize: '.875rem', fontFamily: 'inherit', padding: '8px', fontWeight: 600,
          }}>
            ← Voltar ao carrinho
          </button>
        </div>
      </div>
    </div>
  );
}
